import { Alert, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { readLicence } from '../../api/business/endpoints';
import { useSession } from '../../auth/SessionContext';
import { usePermissions } from '../../auth/usePermissions';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate } from '../../components/format';
import { LicenceStateChip } from '../../components/LicenceStateChip';
import { PageHeader } from '../../components/PageHeader';
import { UsageMeters } from '../../components/UsageMeters';
import { useLanguage } from '../../i18n/LanguageContext';

/** The business's home: who it is, whether it may write, and how much of its licence it uses (US-12). */
export const OverviewPage = () => {
  const { language, t } = useLanguage();
  const { profile } = useSession();
  const permissions = usePermissions();
  const canReadLicence = permissions.can('licence.read');
  const query = useQuery({ queryKey: ['licence-usage'], queryFn: readLicence, enabled: canReadLicence });
  const user = profile?.kind === 'business' ? profile.user : null;

  return (
    <>
      <PageHeader
        title={user?.tenant.name ?? t('overview.title')}
        subtitle={t('overview.greeting', { name: user?.displayName ?? user?.email ?? '' })}
      />
      {user?.licence.state === 'AWAITING_PAYMENT' ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 3 }}
          action={
            permissions.can('billing.read') ? (
              <Button component={RouterLink} to="/billing" color="inherit" size="small">
                {t('overview.toBilling')}
              </Button>
            ) : undefined
          }
        >
          {t('overview.awaitingPayment')}
        </Alert>
      ) : !permissions.writable ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
          {t('overview.readOnly')}
        </Alert>
      ) : null}

      {canReadLicence ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
          {query.isLoading ? <Skeleton variant="rounded" height={140} /> : null}
          {query.data ? (
            <>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">{t('overview.licence')}</Typography>
                <LicenceStateChip state={query.data.state} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {query.data.validUntil
                  ? t('overview.validity', {
                      from: formatDate(query.data.validFrom, language),
                      until: formatDate(query.data.validUntil, language),
                    })
                  : t('overview.validityOpen', { from: formatDate(query.data.validFrom, language) })}
              </Typography>
              <UsageMeters usage={query.data.usage} limits={query.data.limits} />
            </>
          ) : null}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('overview.welcome')}
          </Typography>
        </Paper>
      )}
    </>
  );
};
